import type {ComponentProps, RichTextData} from '@enonic/react-components'
import * as React from 'react'
import {Section, Container, Prose, ButtonLink} from '../primitives'
import {Icon} from '../primitives/Icon'

const STRINGS = {
  buttonDefault: 'Søk opptak',
  pickerLabel: 'Velg hvor vil du søke:',
  pickerAria: 'Velg studiested',
  infoLinePrefix: 'Oppstart i ',
  infoLineConnector: ' er ',
}

interface LocationData {
  name: string
  date: string
  url: string
  newTab: boolean
}

export interface AdmissionByLocationData extends Record<string, unknown> {
  heading: string
  body?: RichTextData
  locations: LocationData[]
  buttonLabel: string
  backgroundColor: 'none' | 'light-blue' | 'light-peach' | 'dark-blue'
}

const bgMap: Record<AdmissionByLocationData['backgroundColor'], string> = {
  'none': '',
  'light-blue': 'bg-slate-100',
  'light-peach': 'bg-peach-100',
  'dark-blue': 'bg-blue-900',
}

function pillLabelClasses(isDark: boolean): string {
  const base =
    'relative inline-flex items-center gap-2 h-10 px-5 rounded-full border cursor-pointer text-base select-none ' +
    'peer-checked:bg-blue-300 peer-checked:border-black peer-checked:text-black ' +
    'peer-focus-visible:outline-1 peer-focus-visible:outline-current'
  return isDark
    ? `${base} border-slate-300 text-slate-300`
    : `${base} border-slate-900 text-slate-900`
}

export const AdmissionByLocation = (props: ComponentProps) => {
  const {heading, body, locations, buttonLabel, backgroundColor} = props.data as AdmissionByLocationData
  const isDark = backgroundColor === 'dark-blue'
  const groupName = React.useId()
  const [selected, setSelected] = React.useState(0)

  const current = locations[selected]

  const textColor = isDark ? 'text-white' : 'text-black'
  const label = buttonLabel || STRINGS.buttonDefault

  return (
    <Section background={bgMap[backgroundColor]}>
      <Container>
        <div className="col-span-full md:col-start-3 md:col-span-8 flex flex-col gap-8">
          {heading && (
            <h2 className={`text-4xl leading-normal ${textColor}`}>{heading}</h2>
          )}
          {body && (
            <Prose
              data={body}
              meta={props.meta}
              common={props.common}
              component={props.component}
              className={`prose-flush ${isDark ? 'prose-invert' : ''}`.trim()}
            />
          )}
          {locations.length > 0 && current && (
            <>
              <div className="flex flex-col gap-6">
                <h3 className={`text-2xl font-medium leading-normal ${textColor}`}>
                  {STRINGS.pickerLabel}
                </h3>
                <fieldset className="m-0 p-0 border-0">
                  <legend className="sr-only">{STRINGS.pickerAria}</legend>
                  <div className="flex flex-wrap gap-2">
                    {locations.map((loc, i) => (
                      <label key={i} className="inline-flex">
                        <input
                          type="radio"
                          name={groupName}
                          value={i}
                          checked={selected === i}
                          onChange={() => setSelected(i)}
                          className="sr-only peer"
                        />
                        <span className={pillLabelClasses(isDark)}>
                          {selected === i && (
                            <Icon name="check" size={16} />
                          )}
                          <span>{loc.name}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <p aria-live="polite" className={`text-lg leading-normal ${textColor}`}>
                  {STRINGS.infoLinePrefix}
                  {current.name}
                  {STRINGS.infoLineConnector}
                  <strong>{current.date}</strong>
                </p>
              </div>
              <ButtonLink
                href={current.url}
                label={label}
                newTab={current.newTab}
                variant="button"
              />
            </>
          )}
        </div>
      </Container>
    </Section>
  )
}
